'use client'

import { useUser } from '@auth0/nextjs-auth0'
import { useActionState, useEffect, useState, useRef } from 'react'

import { updateAccountAction } from '@/app/account/update-my-details/actions/update-account-action'

import { getAccountFields } from '@/app/account/update-my-details/client/fields'

import type {
  AccountFields,
  AccountFieldNames,
} from '@/app/account/update-my-details/client/fields'

import {
  FormField,
  TextInput,
  EmailInput,
  SelectInput,
  CheckboxInput,
  GroupedCheckboxInput,
  RadioInput,
  TextareaInput,
} from '@/app/account/update-my-details/client/forms'

import {
  AccountFormState,
  initialAccountFormState,
} from '@/app/account/update-my-details/shared/account-form-state'

export function AccountFormWrapper({
  fields = {},
  submitText = 'Submit',
  handleSuccess,
  footerLeftChildren = null,
}: {
  fields?: AccountFields
  submitText?: string
  handleSuccess?: () => void
  footerLeftChildren?: React.ReactNode
}) {
  const [initialState, setInitialState] = useState<AccountFormState>(null)

  const { user, isLoading } = useUser()

  // Add user meta to the form state on mount
  useEffect(() => {
    if (user && !isLoading) {
      const userMeta = user?.meta || {}

      setInitialState({
        ...initialAccountFormState,
        ...userMeta,
      })
    }
  }, [user, isLoading])

  if (!initialState) {
    return (
      <div className="py-l text-center">
        <h6>Loading...</h6>
      </div>
    )
  }

  return (
    <AccountForm
      initialState={initialState}
      submitText={submitText}
      fields={fields}
      handleSuccess={handleSuccess}
      footerLeftChildren={footerLeftChildren}
    />
  )
}

export type displayFieldProps = {
  [key: string]: {
    label?: string
    description?: string
    type?: string
    choices?: { label: string; value: string }[] | string[]
  }
}

function AccountForm({
  initialState,
  submitText = 'Submit',
  fields = {},
  handleSuccess,
  footerLeftChildren = null,
}: {
  initialState: AccountFormState
  submitText?: string
  fields?: displayFieldProps
  footerLeftChildren?: React.ReactNode
  handleSuccess?: () => void
}) {
  const [accountFields, setAccountFields] = useState<Record<string, any>>({})
  const [state, formAction, pending] = useActionState(updateAccountAction, initialState)
  const formAnchorRef = useRef(null)

  // Load the account fields on mount
  useEffect(() => {
    getAccountFields().then(setAccountFields)
  }, [])

  // Scroll to the top of the form when the form is submitted
  useEffect(() => {
    if (state.success && formAnchorRef.current) {
      formAnchorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })

      handleSuccess?.()
    }
  }, [state.success])

  // Generate our fields based on the fields passed in, keeping the keys
  const displayFields = Object.keys(fields).length
    ? Object.keys(fields).reduce(
        (acc, key) => {
          acc[key] = {
            ...accountFields[key as string],
            ...fields[key as string],
          }
          return acc
        },
        {} as Record<string, any>,
      )
    : accountFields

  // Show loading state until accountFields are loaded
  if (!Object.keys(accountFields).length) {
    return (
      <div className="py-l text-center">
        <h6>Loading...</h6>
      </div>
    )
  }

  return (
    <div className="w-full @container">
      <div className="anchor" ref={formAnchorRef}></div>
      {state.success ? (
        <div className="py-m">
          <div className="notice notice--success">
            <p>Your account details have been successfully updated.</p>
          </div>
        </div>
      ) : null}

      {state.errors.length ? (
        <div className="py-m">
          <div className="notice notice--error">
            <p>Your submission contains errors. Please review and fix before trying again.</p>
          </div>
        </div>
      ) : null}

      {state.apiError ? (
        <div className="py-m">
          <div className="notice notice--error">{state.apiError}</div>
        </div>
      ) : null}

      <form action={formAction}>
        <div className="grid grid-cols-2 gap-m s:grid-cols-1">
          {Object.keys(displayFields).map((name) => {
            const input = (() => {
              const { placeholder, required } = displayFields[name]

              switch (displayFields[name].type) {
                case 'text':
                  return (
                    <TextInput
                      name={name}
                      placeholder={placeholder}
                      defaultValue={state[name]}
                      required={required}
                      className="input input--text"
                    />
                  )
                case 'email':
                  return (
                    <EmailInput
                      name={name}
                      placeholder={placeholder}
                      defaultValue={state[name]}
                      required={required}
                      className="input input--text"
                    />
                  )
                case 'textarea':
                  return (
                    <TextareaInput
                      name={name}
                      placeholder={placeholder}
                      defaultValue={state[name]}
                      required={required}
                      className="input input--text"
                    />
                  )
                case 'select':
                  return (
                    <SelectInput
                      name={name}
                      defaultValue={state[name]}
                      className="input input--text"
                      required={required}
                      choices={displayFields[name].choices}
                    />
                  )
                case 'checkbox':
                  return (
                    <CheckboxInput
                      name={name}
                      defaultValue={state[name]}
                      classNames={displayFields[name].classNames}
                      choices={displayFields[name].choices}
                    />
                  )
                case 'grouped_checkbox':
                  return (
                    <GroupedCheckboxInput
                      name={name}
                      defaultValue={state[name]}
                      classNames={displayFields[name].classNames}
                      choices={displayFields[name].choices}
                    />
                  )
                case 'radio':
                  return (
                    <RadioInput
                      name={name}
                      defaultValue={state[name]}
                      className="input input--text"
                      choices={displayFields[name].choices}
                    />
                  )
                default:
                  return null
              }
            })()

            return (
              <FormField
                key={name}
                name={name}
                {...displayFields[name]}
                defaultValue={state[name]}
                error={state.errors[name] || null}>
                {input}
              </FormField>
            )
          })}
        </div>

        <div className="form-footer mt-m flex flex-row gap-s border-t pt-m xs:flex-col xs-up:items-center xs-up:justify-between">
          {footerLeftChildren ? <div>{footerLeftChildren}</div> : null}

          <button
            type="submit"
            disabled={pending}
            className="button btn btn--solid ml-auto min-w-[140px] s:grow">
            {pending ? 'Saving...' : submitText}
          </button>
        </div>
      </form>
    </div>
  )
}
